import { nextTick, ref } from 'vue';
import { Contact, ThirdPartyDriver } from 'src/types/overlays';
import { ILocationInfo } from 'src/utils/map';
import { capitalise, validateEmail } from 'src/utils/helpers';
import { Phone } from 'src/types/overlays';
import dayjs from 'dayjs';

export function useOverlays<T extends ThirdPartyDriver | Contact>(contact: T, accountContactPhone?: string) {
  const contactDetails = ref(contact);
  const licensePlateError = ref('');
  const emailError = ref('');
  const phoneError = ref('');
  const dateError = ref('');
  const generalError = ref(false);

  function isThirdPartyDriver(contact: ThirdPartyDriver | Contact): contact is ThirdPartyDriver {
    return (contact as ThirdPartyDriver).vehicle !== undefined && (contact as ThirdPartyDriver).insurance !== undefined;
  }

  function validateLicensePlate(plate: string) {
    nextTick(() => {
      if (!isThirdPartyDriver(contactDetails.value)) return;
      if (!contactDetails.value.vehicle) return;
      contactDetails.value.vehicle.licensePlate = plate.toUpperCase().trim();
      if (plate.length > 7) {
        contactDetails.value.vehicle.licensePlate = plate.slice(0, 7);
      }
      if (/^[A-Za-z0-9]*$/.test(contactDetails.value.vehicle.licensePlate)) {
        licensePlateError.value = '';
        return;
      }

      licensePlateError.value = 'License plate must contains only alphanumeric characters and numbers';
    });
  }

  function validateContactEmail(email?: string) {
    if (validateEmail(email)) {
      emailError.value = '';
      return;
    }

    emailError.value = 'Email must be a valid email address';
  }

  function validatePhoneMatch(phone?: Phone) {
    if (!accountContactPhone || !phone?.formatted) {
      phoneError.value = '';
      return;
    }

    if (phone.formatted === accountContactPhone) {
      phoneError.value = 'Driver phone number cannot be the same as your account contact phone number';
      return;
    }

    phoneError.value = '';
  }

  function validateDate() {
    if (!isThirdPartyDriver(contactDetails.value)) return;
    if (!contactDetails.value?.insurance?.validFrom || !contactDetails.value?.insurance?.validTo) {
      dateError.value = '';
      return;
    }
    if (
      dayjs(contactDetails.value.insurance.validTo).isAfter(dayjs(contactDetails.value.insurance.validFrom)) ||
      dayjs(contactDetails.value.insurance.validTo).isSame(dayjs(contactDetails.value.insurance.validFrom))
    ) {
      dateError.value = '';
      return;
    }

    dateError.value = 'Valid to must be after valid from';
  }

  function setError(error: boolean) {
    generalError.value = error;
  }

  function locationChange(place: ILocationInfo) {
    contactDetails.value.address = place;
  }

  function phoneChange(phone: Phone) {
    contactDetails.value.phone = phone;
    validatePhoneMatch(phone);
  }

  function capitaliseName() {
    contactDetails.value.firstName = capitalise(contactDetails.value.firstName || '');
    contactDetails.value.lastName = capitalise(contactDetails.value.lastName || '');
  }

  return {
    contactDetails,
    licensePlateError,
    emailError,
    phoneError,
    dateError,
    generalError,
    validateLicensePlate,
    validateEmail: validateContactEmail,
    validatePhoneMatch,
    validateDate,
    setError,
    locationChange,
    phoneChange,
    capitaliseName,
  };
}
